import React, { VFC, useCallback, useContext, useMemo, useState } from 'react';

import { Interpolate, Text } from 'mintel-react-ui';
import { useRouter } from 'wouter';

import { SeriesItem as SeriesItemType, TranslatableString } from '/types';
import { MetadataContext } from '/utils/contexts';
import useTranslate, { Trans, i18n } from '/utils/translations';
import useMonthNames from '/utils/useMonthNames';

import styles from './SeriesNav.scss';

const NUM_BEFORE_SHOW_MORE = 5;

type SeriesItemProps = {
  seriesItem: SeriesItemType;
  monthNames: TranslatableString[];
  currentId: string;
  now: Date;
};

const SeriesItem: VFC<SeriesItemProps> = ({
  seriesItem,
  monthNames,
  currentId,
  now,
}) => {
  const { dateDisplay, id } = seriesItem;
  const t = useTranslate();
  const { base } = useRouter();
  const { link, text } = useMemo(() => {
    const year = dateDisplay.getFullYear();
    const month = monthNames[dateDisplay.getMonth()];
    const upcoming = dateDisplay > now;
    if (id == currentId) {
      return {
        link: false,
        text: (
          <Interpolate
            template={t('{year} (report currently in view)')}
            values={{ year }}
          />
        ),
      };
    } else if (upcoming) {
      return {
        link: false,
        text: (
          <Interpolate
            template={t('{year} (upcoming in {month})')}
            values={{ year, month }}
          />
        ),
      };
    } else {
      return {
        link: true,
        text: dateDisplay.toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'long',
        }),
      };
    }
  }, [dateDisplay, id, currentId, monthNames, t, now]);

  if (link) {
    return (
      <a
        className={styles['link-item']}
        href={`${base}/article/${id}`}
        rel="noreferrer"
        target="_blank"
      >
        {text}
      </a>
    );
  }
  return <div className={styles['non-link-item']}>{text}</div>;
};

type SeriesNavProps = {
  data: SeriesItemType[];
  now: Date;
  title: string;
};

const SeriesNav: VFC<SeriesNavProps> = ({ data, now, title }) => {
  const t = useTranslate();
  const [showMore, setShowMore] = useState(false);
  const monthNames = useMonthNames();
  const { id } = useContext(MetadataContext);

  const toggleShowMore = useCallback(() => {
    setShowMore((x) => !x);
  }, [setShowMore]);

  const remaining = Math.max(data.length - NUM_BEFORE_SHOW_MORE, 0);
  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <Text.Heading5 darkBg>{title}</Text.Heading5>
      </div>
      <div>
        <div>
          {data
            .slice(undefined, showMore ? undefined : NUM_BEFORE_SHOW_MORE)
            .map((seriesItem) => (
              <SeriesItem
                seriesItem={seriesItem}
                monthNames={monthNames}
                currentId={id}
                now={now}
                key={seriesItem.id}
              />
            ))}
        </div>
        {!!remaining && (
          <button
            className={styles['show-more']}
            onClick={toggleShowMore}
            tabIndex={0}
          >
            {showMore ? (
              <Trans>Show less</Trans>
            ) : (
              <Interpolate
                template={t('Show {remaining} more')}
                values={{ remaining }}
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SeriesNav;
